import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import Breadcrumb from '../components/common/Breadcrumb';

const LEVEL_COLORS = {
  1: 'text-gray-400', 2: 'text-gray-300', 3: 'text-blue-400',
  4: 'text-cyan-400', 5: 'text-safe', 6: 'text-yellow-400',
  7: 'text-orange-400', 8: 'text-danger', 9: 'text-purple-400',
};

function RankBadge({ rank }) {
  if (rank === 1) return <span className="text-xl">&#x1F947;</span>;
  if (rank === 2) return <span className="text-xl">&#x1F948;</span>;
  if (rank === 3) return <span className="text-xl">&#x1F949;</span>;
  return <span className="text-gray-500 font-mono text-sm w-6 text-center">#{rank}</span>;
}

function AvatarCircle({ pseudo, color, size = 'w-10 h-10' }) {
  const initial = (pseudo || '?')[0].toUpperCase();
  return (
    <div
      className={`${size} rounded-full flex items-center justify-center font-bold text-white text-sm`}
      style={{ backgroundColor: color || '#00d4aa' }}
    >
      {initial}
    </div>
  );
}

function StatCard({ value, label, color = 'text-white' }) {
  return (
    <div className="glass-card p-5 text-center">
      <div className={`text-3xl font-bold ${color} mb-1`}>{value}</div>
      <div className="text-gray-400 text-sm">{label}</div>
    </div>
  );
}

export default function CommunityPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [period, setPeriod] = useState('all');
  const [showRegister, setShowRegister] = useState(false);
  const [registerForm, setRegisterForm] = useState({ pseudo: '', email: '', avatar_color: '#00d4aa' });
  const [registerMsg, setRegisterMsg] = useState(null);

  useEffect(() => {
    document.title = `${t('community.title')} | SpamSandbox`;
    api.communityStats().then(r => setStats(r.data)).catch(() => {});
    return () => { document.title = 'SpamSandbox'; };
  }, []);

  useEffect(() => {
    api.communityLeaderboard({ period, limit: 20 })
      .then(r => setLeaderboard(r.data?.leaderboard || []))
      .catch(() => {});
  }, [period]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterMsg(null);
    try {
      const res = await api.communityRegister(registerForm);
      setRegisterMsg({ type: 'success', text: t('community.welcomeMsg', { pseudo: res.data.pseudo, defaultValue: `Welcome ${res.data.pseudo}! You are now a community member.` }) });
      setShowRegister(false);
      // Refresh
      api.communityStats().then(r => setStats(r.data)).catch(() => {});
      api.communityLeaderboard({ period, limit: 20 }).then(r => setLeaderboard(r.data?.leaderboard || [])).catch(() => {});
    } catch (err) {
      setRegisterMsg({ type: 'error', text: err.message || t('common.error') });
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <Breadcrumb items={[{ label: t('breadcrumb.home'), to: '/' }, { label: t('breadcrumb.community') }]} />
      {/* Header */}
      <div className="text-center mb-10 mt-4">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{t('community.title')}</h1>
        <p className="text-gray-400 max-w-2xl mx-auto">
          {t('community.subtitle')}
        </p>
        <button
          onClick={() => setShowRegister(!showRegister)}
          className="btn-primary mt-4"
        >
          {t('community.register')}
        </button>
      </div>

      {/* Register form */}
      {showRegister && (
        <div className="glass-card p-6 mb-8 max-w-md mx-auto">
          <h2 className="text-white font-semibold text-lg mb-4">{t('community.registerTitle')}</h2>
          <form onSubmit={handleRegister} className="space-y-3">
            <input
              type="text" placeholder={t('community.pseudoLabel')}
              value={registerForm.pseudo}
              onChange={e => setRegisterForm({ ...registerForm, pseudo: e.target.value })}
              className="w-full bg-dark-surface border border-dark-border rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-safe focus:outline-none"
              required minLength={3} maxLength={30} pattern="[a-zA-Z0-9_\-]+"
            />
            <input
              type="email" placeholder={t('community.emailLabel')}
              value={registerForm.email}
              onChange={e => setRegisterForm({ ...registerForm, email: e.target.value })}
              className="w-full bg-dark-surface border border-dark-border rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-safe focus:outline-none"
              required
            />
            <div className="flex items-center gap-3">
              <label className="text-gray-400 text-sm">{t('community.colorLabel', { defaultValue: 'Color:' })}</label>
              <input
                type="color" value={registerForm.avatar_color}
                onChange={e => setRegisterForm({ ...registerForm, avatar_color: e.target.value })}
                className="w-8 h-8 rounded cursor-pointer bg-transparent border-0"
              />
              <AvatarCircle pseudo={registerForm.pseudo || '?'} color={registerForm.avatar_color} />
            </div>
            <button type="submit" className="btn-primary w-full">{t('community.registerBtn')}</button>
          </form>
        </div>
      )}

      {registerMsg && (
        <div className={`text-center text-sm mb-6 ${registerMsg.type === 'success' ? 'text-safe' : 'text-danger'}`}>
          {registerMsg.text}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard value={stats.total_contributors} label={t('community.contributors', { defaultValue: 'Contributors' })} color="text-safe" />
          <StatCard value={stats.total_reports} label={t('community.totalReports')} color="text-white" />
          <StatCard value={stats.verified_reports} label={t('community.verifiedReports')} color="text-cyan-400" />
          <StatCard value={`${Math.round(stats.total_money_lost).toLocaleString()} EUR`} label={t('community.moneyReported')} color="text-danger" />
        </div>
      )}

      {/* Leaderboard */}
      <div className="glass-card p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">{t('community.leaderboard')}</h2>
          <div className="flex gap-2">
            {[
              { key: 'all', label: t('community.periodAll') },
              { key: 'month', label: t('community.periodMonth') },
              { key: 'week', label: t('community.periodWeek') },
            ].map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                  period === p.key
                    ? 'bg-safe/20 text-safe border border-safe/30'
                    : 'bg-dark-surface text-gray-400 border border-dark-border hover:text-white'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {leaderboard.length === 0 ? (
          <p className="text-center text-gray-500 py-8">{t('community.noContributors')}</p>
        ) : (
          <div className="space-y-2">
            {leaderboard.map((user) => (
              <Link
                key={user.pseudo}
                to={`/community/${user.pseudo}`}
                className="flex items-center gap-4 p-3 rounded-lg hover:bg-dark-surface/50 transition-all group"
              >
                <div className="w-8 flex justify-center">
                  <RankBadge rank={user.rank} />
                </div>
                <AvatarCircle pseudo={user.pseudo} color={user.avatar_color} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium group-hover:text-safe transition-colors">{user.pseudo}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${LEVEL_COLORS[user.level] || 'text-gray-400'} bg-dark-surface border border-dark-border`}>
                      Lvl {user.level} - {user.level_name}
                    </span>
                  </div>
                  <div className="text-gray-500 text-xs">
                    {user.total_reports} signalement(s) | {user.verified_reports} verifie(s) | {user.badge_count} badge(s)
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-safe font-bold font-mono">{user.xp.toLocaleString()} XP</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Recent reports */}
      {stats?.recent_reports?.length > 0 && (
        <div className="glass-card p-6">
          <h2 className="text-xl font-bold text-white mb-4">{t('community.recentReports', { defaultValue: 'Recent reports' })}</h2>
          <div className="space-y-3">
            {stats.recent_reports.map((report) => (
              <div key={report.id} className="flex items-start gap-3 p-3 bg-dark-surface/30 rounded-lg">
                {report.profile_mini ? (
                  <AvatarCircle pseudo={report.profile_mini.pseudo} color={report.profile_mini.avatar_color} size="w-8 h-8" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-gray-400 text-xs">?</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white text-sm font-medium">
                      {report.profile_mini?.pseudo || report.author_name || t('common.anonymous')}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      report.report_type === 'scam' ? 'bg-danger/20 text-danger' :
                      report.report_type === 'phishing' ? 'bg-orange-500/20 text-orange-400' :
                      report.report_type === 'legitimate' ? 'bg-safe/20 text-safe' :
                      'bg-gray-600/20 text-gray-400'
                    }`}>
                      {report.report_type}
                    </span>
                    {report.is_verified && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400">{t('community.verifiedReports')}</span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm truncate">{report.sender_domain} — {report.description}</p>
                  {report.money_lost > 0 && (
                    <p className="text-danger text-xs mt-1">{report.money_lost} {report.currency} {t('community.lost', { defaultValue: 'lost' })}</p>
                  )}
                </div>
                <div className="text-gray-500 text-xs whitespace-nowrap">
                  {new Date(report.created_at).toLocaleDateString('fr-FR')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
